import { resolveCaptionFont } from '@src/components/sidepanel/KaraokeSettings/resolveCaptionFont';
import { useKaraokeSceneTextPosition } from '@src/components/sidepanel/KaraokeSettings/useKaraokeSceneTextPosition';
import { useCaptionsToggle } from '@src/components/transcript/hooks/useCaptionsToggle';
import { useKaraoke } from '@src/hooks/useKaraoke';
import { DEFAULT_KARAOKE_CONFIG } from '@src/services/karaoke/karaokeConstants';
import { useKaraokeStore } from '@src/stores-v2';
import { getMinMaxYPosition } from '@src/utility/videoTranscriptionUtils';

import type { FloatingToolbarHost } from './FloatingToolbarHost';
import type { CaptionFontVariant } from '@src/components/sidepanel/KaraokeSettings/resolveCaptionFont';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { CaptionFallbackTemplate } from '@src/editor/services/applyCaptionsLanguage';

export type CaptionPositionSnap = 'top' | 'middle' | 'bottom';
export type CaptionPositionSnaps = Record<CaptionPositionSnap, number>;

/**
 * PLAT-12100 DS5 — Top / Middle / Bottom over the caption's `y` (a percentage
 * of the canvas height, 0 = top), taken from the Captions panel's own
 * Vertical Position slider: its top bound, its Center Point snap, and its
 * Safe Zone snap (the aspect ratio's default `y`). `minMaxYPosition` is
 * `getMinMaxYPosition(...)`, which the slider inverts into `y` bounds.
 */
export function captionPositionSnaps(
  minMaxYPosition: readonly [number, number] | number[],
  safeZoneY: number,
): CaptionPositionSnaps {
  const top = 100 - minMaxYPosition[1];
  const bottomBound = 100 - minMaxYPosition[0];
  return { top, middle: (top + bottomBound) / 2, bottom: safeZoneY };
}

/** The snap the caption sits on, to the half percent; `null` when custom. */
export function activeCaptionSnap(
  y: number,
  snaps: CaptionPositionSnaps,
): CaptionPositionSnap | null {
  const match = (Object.keys(snaps) as CaptionPositionSnap[]).find(
    snap => Math.abs(snaps[snap] - y) < 0.5,
  );
  return match ?? null;
}

/**
 * Everything the captions bar reads and writes, through the same functions
 * the Captions panel uses (PLAT-12100 DS2 / DS3), so the two always agree:
 * the selected caption clip's config (else the default), style edits through
 * `updateKaraokeConfigAndProduce`, position through the scene-aware
 * `updateTextPosition`, templates through `useCaptionsToggle().applyTemplate`.
 * Every write is one undo step.
 *
 * It reads the surface through its host (PLAT-12101); the caption store
 * comes from the surrounding store scope, as it does for the panel and the
 * clip preview's Captions picker. `config` is `null` when there is nothing to
 * edit.
 */
export function useCaptionsBarController(
  host: FloatingToolbarHost,
  activeCaptionId: string | undefined,
) {
  const karaokeStore = useKaraokeStore();
  const { editorStore, captions: surface } = host;
  const { getKaraokeConfigForKaraokeClipId, updateKaraokeConfigAndProduce } =
    useKaraoke(editorStore.editor);
  const { textBrandingService } = surface;

  const activeLine = activeCaptionId
    ? karaokeStore.karaokeLines.find(line => line.id === activeCaptionId)
    : undefined;
  const config: KaraokeConfig | null =
    (activeLine
      ? getKaraokeConfigForKaraokeClipId(activeLine.id)
      : karaokeStore.defaultKaraokeConfig) ?? null;

  const modify = (patch: Partial<KaraokeConfig>) => {
    if (!config) return;
    textBrandingService.checkPropsChanged('karaoke', patch);
    updateKaraokeConfigAndProduce({ ...config, ...patch });
  };

  const setFont = (
    fontScriptName: string | undefined,
    variant: CaptionFontVariant | undefined,
  ) => {
    if (!config) return;
    modify(
      resolveCaptionFont(
        fontScriptName,
        variant,
        config.font_weight,
        surface.customFonts,
      ),
    );
  };

  const { textPositionEffective, updateTextPosition } =
    useKaraokeSceneTextPosition({
      editorStore,
      currentTime: surface.time,
      // Nothing to edit yet: the defaults keep the hook's inputs well-typed;
      // `setPosition` is a no-op without a config.
      basePosition: {
        x: config?.x ?? DEFAULT_KARAOKE_CONFIG.x,
        y: config?.y ?? DEFAULT_KARAOKE_CONFIG.y,
        x_alignment: config?.x_alignment ?? DEFAULT_KARAOKE_CONFIG.x_alignment,
      } as Pick<KaraokeConfig, 'x' | 'y' | 'x_alignment'>,
      preferredElementId: surface.originalVideoElementIdAtTime,
      sceneLayoutIndexStore: surface.sceneLayoutIndexStore,
    });

  const snaps = config
    ? captionPositionSnaps(
        getMinMaxYPosition(
          undefined,
          String(config.font_size),
          (config as { instagramLines?: 1 | 2 | 3 | 4 | 5 | 6 | 7 })
            .instagramLines || 5,
          config,
        ),
        parseFloat(
          surface.karaokeProducer.getKaraokeTextSettingByAspectRatio().y ||
            DEFAULT_KARAOKE_CONFIG.y ||
            '86',
        ),
      )
    : null;
  const y = parseFloat(String(textPositionEffective.y ?? config?.y ?? ''));
  const position =
    snaps && !Number.isNaN(y) ? activeCaptionSnap(y, snaps) : null;
  const setPosition = (snap: CaptionPositionSnap) => {
    if (!snaps) return;
    updateTextPosition('y', snaps[snap], modify);
  };

  const captions = useCaptionsToggle(surface.toggle);
  const { aspectRatio } = surface;
  // Read on every render: the list comes from the observable organization, so
  // a template saved in the panel shows up while the bar stays open.
  const templates =
    textBrandingService.getCustomKaraokeTemplateConfigs(aspectRatio);
  // A template rebuilds the caption track with new ids, so the selected
  // caption stops existing; select the new one at the same time to keep the
  // bar open.
  const applyTemplate = (template: CaptionFallbackTemplate) => {
    const clips = () => editorStore.editor?.getKaraokeTrack()?.karaokeClips;
    const at = clips()
      ?.find(clip => clip.id === activeCaptionId)
      ?.getKaraokeStartTime();
    captions.applyTemplate(template);
    if (at == null) return;
    const next = clips()?.find(clip => {
      const start = clip.getKaraokeStartTime() ?? 0;
      return start <= at && at < start + (clip.getKaraokeDuration() ?? 0);
    });
    if (next && next.id !== activeCaptionId) host.selectElement(next.id);
  };

  const templateTitle =
    templates.find(t => t.templateId === captions.templateId)?.title ??
    textBrandingService.getKaraokeTemplateTitle(captions.templateId) ??
    'Style';

  return {
    config,
    modify,
    setFont,
    position,
    setPosition,
    templates,
    templateId: captions.templateId,
    templateTitle,
    applyTemplate,
  };
}
